/* eslint-disable react/no-array-index-key */
import Box from '@amzn/meridian/box';
import Checkbox from '@amzn/meridian/checkbox';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import React, { useEffect, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatGoalToString } from '../../Goals/helper';
import DataState from '../../../Types/DataState';
import Alert from '@amzn/meridian/alert';
import { Goal, GoalStatus } from '@amzn/aet-galactus-kit';
import { StateContext } from '../../../GlobalStateStore';

interface AddGoalsContainerProps {
  goalsState: string;
  goals: Goal[];
  disabled: boolean;
  selectedGoals: string[];
  setSelectedGoals: any;
  noteId: string;
}

export const Container: React.FC<AddGoalsContainerProps> = ({
  goalsState,
  goals,
  disabled,
  selectedGoals,
  setSelectedGoals,
  noteId,
}: AddGoalsContainerProps): React.ReactElement => {
  const [checkBoxValues, setCheckBoxValues] = React.useState<boolean[]>(
    Array.from({ length: goals.length }, () => false)
  );
  const [openGoals, setOpenGoals] = React.useState<Goal[]>([]);

  const intl = useIntl();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { isTm, requesterUserID, requesterUsername } = inspireOptions.data;

  useEffect(() => {
    if (goalsState === DataState.LOADED) {
      setOpenGoals(goals.filter(g => g.status === GoalStatus.Open));
    }
  }, [goalsState, goals]);

  useEffect(() => {
    setCheckBoxValues(
      openGoals.map(goal => selectedGoals.includes(goal.goalID))
    );
  }, [openGoals]);

  return goalsState === DataState.LOADING ? (
    <Loader type='circular' />
  ) : goalsState === DataState.ERROR ? (
    <Alert type='error'>
      <FormattedMessage
        id='cs_perfportal_error_refresh_page'
        defaultMessage='Something went wrong. Please try refreshing the page. 
    If the problem persists, please submit a SIM ticket for us to troubleshoot.'
      />
    </Alert>
  ) : openGoals.length === 0 ? (
    <FormattedMessage
      id='cs_perfportal_no_goals'
      defaultMessage='No goals assigned to this associate yet. Please create a new goal.'
      description='No goals assigned'
    />
  ) : (
    <Box>
      {openGoals.map((goal, idx) => {
        return (
          <Checkbox
            disabled={disabled}
            checked={checkBoxValues[idx]}
            onChange={checked => {
              const newCheckBoxValues = [...checkBoxValues];
              newCheckBoxValues[idx] = !newCheckBoxValues[idx];
              setCheckBoxValues(newCheckBoxValues);
              if (checked) {
                setSelectedGoals([...selectedGoals, goal.goalID]);
              } else {
                const filteredGoals = selectedGoals.filter(
                  selectedGoal => selectedGoal !== goal.goalID
                );
                setSelectedGoals(filteredGoals);
              }
            }}
            data-csa-c-type='checkbox'
            data-csa-c-slot-id={`coaching-form-goal-setting-goal-checkbox`}
            data-csa-c-content-id={`coaching-form-goal-setting-goal-checkbox-for-${goal.goalID}-${noteId}`}
            data-csa-c-tag1='Team'
            data-csa-c-tag2='Coaching Form'
            data-csa-c-tag3='Goal Addition'
            data-csa-c-profile-id={requesterUserID}
            data-csa-c-username={requesterUsername}
            data-csa-c-is-tm={isTm}
            data-cy='add-goal-options'
          >
            <Text>{formatGoalToString(goal, intl)}</Text>
          </Checkbox>
        );
      })}
    </Box>
  );
};

export default Container;
